// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateAccountSettings from "@insite/client-framework/Store/Pages/AccountSettings/Handlers/UpdateAccountSettings";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import { LinkPresentationProps } from "@insite/mobius/Link/Link";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { placeHolders } from "@conklin/XDL/constants";
import React, { ChangeEvent } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    updateAccountSettings,
};

const mapStateToProps = (state: ApplicationState) => ({
    editingAccount: state.pages.accountSettings.editingAccount,
    emailErrorMessage: state.pages.accountSettings.emailErrorMessage,
    useEmailAsUserName: getSettingsCollection(state).accountSettings.useEmailAsUserName,
    changePasswordUrl: getPageLinkByPageType(state, "ChangePasswordPage")?.url,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AccountSettingsUserInformationStyles {
    userInformationTitle: TypographyProps;
    userInformationGridItem?: GridItemProps;
    changePasswordLink?: LinkPresentationProps;
    // XNG-Changes
    container?: InjectableCss;
    titleWrapper?: InjectableCss;
    inputsWrapper?: InjectableCss;
    passwordWrapper?: InjectableCss;
}

export const userInformationStyles: AccountSettingsUserInformationStyles = {
    userInformationGridItem: {
        width: 12,
    },
    changePasswordLink: {
        css: css`
            margin-left: 10px;
        `,
    },
    userInformationTitle: {
        variant: "h2",
    },
};

const styles = userInformationStyles;
const password = "*************";
// Xng-Changes
const formId = "accountSettingsForm";

const AccountSettingsUserInformation = ({
    editingAccount,
    useEmailAsUserName,
    changePasswordUrl,
    emailErrorMessage,
    updateAccountSettings,
}: Props) => {
    if (!editingAccount) {
        return null;
    }

    const fullName = `${editingAccount.firstName} ${editingAccount.lastName}`;
    const emailChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        updateAccountSettings({ email: event.currentTarget.value });
    };

    // const label = (
    //     <>
    //         <Typography>{translate("Password")}</Typography>
    //         <Link {...styles.changePasswordLink} href={changePasswordUrl}>
    //             {translate("Change")}
    //         </Link>
    //     </>
    // );

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.titleWrapper}>
                <Typography {...styles.userInformationTitle}>{translate("Your Information")}</Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.inputsWrapper}>
                {!useEmailAsUserName && (
                    <TextField
                        label={translate("User Name")}
                        value={editingAccount.userName}
                        data-test-selector="accountSettings_userName"
                        disabled
                    />
                )}
                <TextField
                    // XNG-Changes: disable custom validation since it is handled from OOTB
                    type="text"
                    label={translate("Email")}
                    onChange={emailChangeHandler}
                    value={editingAccount.email}
                    data-test-selector="accountSettings_email"
                    required
                    maxLength={256}
                    error={emailErrorMessage}
                    placeholder={placeHolders.email}
                    // Xng-Changes
                    {...{ formId, showError: !!emailErrorMessage }}
                />
                <TextField label={translate("Full Name")} value={fullName} disabled />
                <StyledWrapper {...styles.passwordWrapper}>
                    <TextField type="password" label={translate("Password")} disabled value={password} />
                    <Link {...styles.changePasswordLink} href={changePasswordUrl}>
                        {translate("Change Password")}
                    </Link>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccountSettingsUserInformation),
    definition: {
        allowedContexts: ["AccountSettingsPage"],
        group: "Account Settings",
        displayName: "User Information",
    },
};

export default widgetModule;
